// Empty-state for /browse when the scoped /workspaces returns [].
//
// Pass 6.3.5: role-aware copy. The empty state means different
// things depending on who's looking and at whose scope, so the copy
// branches across four cases (see below). No shell commands appear
// in any branch — onboarding a workspace happens out-of-band (an X11
// SSH login session), not from this UI.
//
//   Case 1 — a regular user with nothing assigned to them.
//   Case 2 — a superadmin viewing their OWN scope (admins own
//            nothing by the 6.3.4 architectural rule).
//   Case 3 — a superadmin viewing a specific user who owns nothing.
//   Case 4 — a superadmin in "All users" scope with an empty system
//            (fresh deploy), or the orphaned-data fallback.

import { Eye, Folder, User, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { EmptyState } from "./ui/EmptyState";
import { useAuth } from "../state/AuthContext";
import { useImpersonation } from "../state/ImpersonationContext";
import { useSystemStatus } from "../state/SystemStatusContext";

interface EmptyBrowseStateProps {
  onLogout: () => void;
}

export function EmptyBrowseState({ onLogout }: EmptyBrowseStateProps) {
  const { role, userId } = useAuth();
  const { current } = useImpersonation();
  const { status } = useSystemStatus();
  const navigate = useNavigate();

  // We only ever render this component when the scoped workspace
  // list is already empty; the branching is purely about WHO is
  // looking and at WHOSE scope.
  const { caseId, node } = ((): {
    caseId: string;
    node: React.ReactNode;
  } => {
    // Case 1 — regular user, nothing assigned. No self-serve action.
    if (role !== "superadmin") {
      return {
        caseId: "user-unassigned",
        node: (
          <EmptyState
            icon={<Users className="w-full h-full" strokeWidth={1.5} />}
            title="No workspaces assigned to you yet"
            description="Contact your administrator to be added to a workspace, or to onboard your own via the login flow."
          />
        ),
      };
    }

    // Superadmin, impersonating someone.
    if (current !== null) {
      // Case 2 — viewing own scope. Admins own nothing by rule.
      if (current.userId === userId) {
        return {
          caseId: "superadmin-self",
          node: (
            <EmptyState
              icon={<Eye className="w-full h-full" strokeWidth={1.5} />}
              title="You don't own any workspaces"
              description="As an administrator, you don't directly own workspaces. Use the scope selector above to view workspaces owned by your team members."
            />
          ),
        };
      }
      // Case 3 — viewing another user who owns nothing.
      return {
        caseId: "superadmin-other-user",
        node: (
          <EmptyState
            icon={<User className="w-full h-full" strokeWidth={1.5} />}
            title={`${current.username} has no workspaces yet`}
            description="This user hasn't been assigned any workspaces. They can run the login flow to archive their first Slack workspace."
          />
        ),
      };
    }

    // Case 4 — superadmin, "All users" scope. With nothing visible
    // here, either the whole system is empty (fresh deploy) or the
    // workspaces that exist have no resolvable owner (orphaned).
    const totalSystemWorkspaces = status?.workspaces.length ?? 0;
    const orphaned = totalSystemWorkspaces > 0;
    return {
      caseId: orphaned ? "system-orphaned" : "system-empty",
      node: (
        <EmptyState
          icon={<Folder className="w-full h-full" strokeWidth={1.5} />}
          title="No workspaces archived yet"
          description={
            orphaned
              ? "No workspaces are visible in this view — the system has data whose owner can't be resolved (orphaned). Add a user via 'User management' to get a clean owner."
              : "Get started by adding your first user via 'User management', then ask them to run the login flow to archive their first Slack workspace."
          }
          action={{
            label: "Go to User management",
            onClick: () => navigate("/users"),
          }}
        />
      ),
    };
  })();

  return (
    <div
      className="h-screen flex flex-col overflow-y-auto"
      data-testid="empty-browse-state"
    >
      <header className="flex items-center justify-between px-page py-loose">
        <h1 className="text-h2">Slack Archive Viewer</h1>
        <button
          type="button"
          onClick={onLogout}
          className="px-3 py-1.5 border border-theme-border-default text-caption text-theme-text-secondary hover:text-theme-text-primary transition-colors duration-150"
        >
          Sign out
        </button>
      </header>
      <div className="flex-1 flex items-center justify-center px-page pb-12">
        <div
          className="w-full max-w-lg"
          data-testid="empty-browse-content"
          data-empty-case={caseId}
        >
          {node}
        </div>
      </div>
    </div>
  );
}
